import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { daysBetween, sessionStart } from '../../utils/progress';

// Intl's short names carry a dot in pt-BR ("out.", "ter."): the app writes them bare
const bare = (text) => text.replace('.', '');

/**
 * Progresso's words for numbers and dates, in the page's language: "57,5 kg × 10", "29 out, 07:04", "ter, 27 de
 * out", "19 a 25 de out". Every string comes from the page's own namespace.
 */
export const useProgressFormat = () => {
  const { t, i18n: { language } } = useTranslation();

  return useMemo(() => {
    const ui = (key, options) => t(`pages.Progress.ui.${key}`, options);
    const isPt = language === 'pt';
    const locale = isPt ? 'pt-BR' : 'en-US';
    const now = new Date();

    const number = (value) => (Math.round(value * 10) / 10).toLocaleString(locale);
    const integer = (value) => Math.round(value).toLocaleString(locale);
    const minutes = (seconds) => `${Math.max(1, Math.round(seconds / 60))} min`;
    // "2h 49" for a week of training, "48 min" under an hour
    const trainingTime = (seconds) => {
      if (!seconds) return '0 min';
      if (seconds < 3600) return minutes(seconds);
      const total = Math.round(seconds / 60);
      return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, '0')}`;
    };

    const month = (date) => bare(date.toLocaleDateString(locale, { month: 'short' }));
    const weekday = (date) => bare(date.toLocaleDateString(locale, { weekday: 'short' }));
    const weekdayLong = (date) => date.toLocaleDateString(locale, { weekday: 'long' });
    const otherYear = (date) => date.getFullYear() !== now.getFullYear();
    const parts = (date) => ({ day: date.getDate(), month: month(date), year: date.getFullYear() });

    // "1 de set" / "Sep 1", with the year when it isn't this one
    const shortDate = (date) => ui(otherYear(date) ? 'date.shortYear' : 'date.short', parts(date));
    // "Hoje", "Ontem", "ter, 27 de out"
    const dayName = (date) => {
      const ago = daysBetween(now, date);
      if (ago === 0) return ui('today');
      if (ago === 1) return ui('yesterday');
      return ui('date.weekday', { weekday: weekday(date), date: shortDate(date) });
    };
    // When a session started: "29 out, 07:04" / "Oct 29, 7:04 AM"
    const startText = (log) => {
      const date = sessionStart(log);
      const time = date.toLocaleTimeString(locale, { hour: isPt ? '2-digit' : 'numeric', minute: '2-digit' });
      return ui(otherYear(date) ? 'date.cardYear' : 'date.card', { ...parts(date), time });
    };
    // "19 a 25 de out" / "28 de set a 4 de out"
    const weekRange = (start, end) => {
      const last = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1);
      if (start.getMonth() === last.getMonth() && start.getFullYear() === last.getFullYear()) {
        return ui('date.range', { start: start.getDate(), end: last.getDate(), month: month(last) });
      }
      return ui('date.rangeMonths', { start: start.getDate(), startMonth: month(start), end: last.getDate(), endMonth: month(last) });
    };
    // Under a chart bar: "26/out" / "Oct 26"
    const weekLabel = (date) => (isPt ? `${String(date.getDate()).padStart(2, '0')}/${month(date)}` : `${month(date)} ${date.getDate()}`);

    // A set: "57,5 kg × 10" (dose), "57,5 kg × 10 reps" (a table row), "57,5×10" (a row of sets)
    const dose = (set) => (set.weight > 0 ? `${number(set.weight)} ${set.unit} × ${set.reps}` : ui('reps', { count: set.reps }));
    const setLine = (set) => (set.weight > 0 ? `${number(set.weight)} ${set.unit} × ${ui('reps', { count: set.reps })}` : ui('reps', { count: set.reps }));
    const compactSet = (set) => (set.weight > 0 ? `${number(set.weight)}×${set.reps}` : `${set.reps}`);

    // "Treino B"; a session whose workout was deleted before names were saved is just "Treino"
    const workoutTitle = (name) => (name ? `${t('standalone.workout')} ${name}` : t('standalone.workout'));

    return {
      ui,
      locale,
      number,
      integer,
      minutes,
      trainingTime,
      weekday,
      weekdayLong,
      shortDate,
      dayName,
      startText,
      weekRange,
      weekLabel,
      dose,
      setLine,
      compactSet,
      workoutTitle,
    };
  }, [t, language]);
};
